import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CalendarCheck, CalendarX, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/book/manage`;

const dayLabel = (stamp) => new Date(stamp).toLocaleDateString(undefined, {
  weekday: "long", day: "numeric", month: "long",
});
const timeLabel = (stamp) => new Date(stamp).toLocaleTimeString(undefined, {
  hour: "2-digit", minute: "2-digit",
});

const shell = "min-h-screen bg-[#F7F4EC] px-6 py-16";
const card = "mt-10 rounded-2xl border border-[#0E1A13]/10 bg-white p-8";

export default function ManageBooking() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("");          // "" | "move" | "cancel"
  const [slot, setSlot] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);        // "cancelled" | "moved"

  useEffect(() => {
    axios.get(`${API}/${bookingId}`, { params: { t: token } })
      .then(({ data: payload }) => setData(payload))
      .catch((e) => setError(e.response?.data?.detail || "That link is not valid any more"));
  }, [bookingId, token]);

  const byDay = useMemo(() => {
    const groups = {};
    (data?.slots || []).forEach((stamp) => {
      const key = stamp.slice(0, 10);
      groups[key] = groups[key] || [];
      groups[key].push(stamp);
    });
    // Ten days is plenty to choose from without turning the page into a wall of buttons.
    return Object.entries(groups).slice(0, 10);
  }, [data]);

  const act = async (path, body) => {
    setBusy(true);
    setError("");
    try {
      const { data: payload } = await axios.post(
        `${API}/${bookingId}/${path}`, body, { params: { t: token } });
      // Keyed off which action was taken, not off the status string the API happens to return.
      setDone(path === "cancel" ? "cancelled" : "moved");
      setData((prev) => ({
        ...prev,
        booking: { ...prev.booking, starts_at: payload.starts_at || prev.booking.starts_at },
      }));
    } catch (e) {
      setError(e.response?.data?.detail || "That did not go through — please try again");
    } finally {
      setBusy(false);
    }
  };

  const pill = (active) => `rounded-full border px-4 py-2 text-sm transition-all duration-200 ${
    active ? "border-[#0E1A13] bg-[#0E1A13] text-[#F7F4EC]"
      : "border-[#0E1A13]/15 bg-white text-[#0E1A13]/75 hover:border-[#C8A24A] hover:-translate-y-0.5"}`;

  const header = (
    <>
      <p className="font-serif text-2xl text-[#0E1A13]">OneHarvestCo. Benefits</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#9A6A3A]">
        Group benefits · Houston, Texas
      </p>
    </>
  );

  if (error && !data) {
    return (
      <div className={shell} data-testid="manage-booking-page">
        <div className="mx-auto max-w-2xl">
          {header}
          <p className="mt-12 text-sm text-[#9A3A2A]" data-testid="manage-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={shell} data-testid="manage-booking-page">
        <div className="mx-auto max-w-2xl">
          {header}
          <p className="mt-12 flex items-center gap-2 text-sm text-[#0E1A13]/50">
            <Loader2 className="h-4 w-4 animate-spin" /> Finding your meeting…
          </p>
        </div>
      </div>
    );
  }

  const { booking } = data;

  if (done === "cancelled") {
    return (
      <div className={shell} data-testid="manage-booking-page">
        <div className="mx-auto max-w-2xl">
          {header}
          <div className={card} data-testid="manage-cancelled">
            <CalendarX className="h-6 w-6 text-[#9A3A2A]" />
            <h1 className="mt-4 font-serif text-3xl font-light text-[#0E1A13]">
              That's cancelled
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-[#0E1A13]/70">
              Your {booking.type_name} is off the diary and a confirmation is on its way. If you'd
              still like to talk another time, reply to that email and I'll sort it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (done === "moved") {
    return (
      <div className={shell} data-testid="manage-booking-page">
        <div className="mx-auto max-w-2xl">
          {header}
          <div className={card} data-testid="manage-moved">
            <CalendarCheck className="h-6 w-6 text-[#C8A24A]" />
            <h1 className="mt-4 font-serif text-3xl font-light text-[#0E1A13]">
              Moved — you're all set
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-[#0E1A13]/70">
              Your {booking.type_name} is now {dayLabel(booking.starts_at)} at{" "}
              {timeLabel(booking.starts_at)}. A fresh confirmation is on its way.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (booking.status !== "booked") {
    return (
      <div className={shell} data-testid="manage-booking-page">
        <div className="mx-auto max-w-2xl">
          {header}
          <div className={card} data-testid="manage-inactive">
            <h1 className="font-serif text-3xl font-light text-[#0E1A13]">
              This meeting is no longer active
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-[#0E1A13]/70">
              It looks like it has already been cancelled. Reply to any of my emails and I'll
              find you a new time.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shell} data-testid="manage-booking-page">
      <div className="mx-auto max-w-2xl">
        {header}

        <h1 className="mt-10 font-serif text-4xl font-light leading-tight text-[#0E1A13] sm:text-5xl"
            data-testid="manage-title">
          Your {booking.type_name.toLowerCase()}
        </h1>
        <p className="mt-4 flex items-center gap-2 text-base text-[#0E1A13]/70"
           data-testid="manage-current-time">
          <Clock className="h-4 w-4 text-[#9A6A3A]" />
          {dayLabel(booking.starts_at)} at {timeLabel(booking.starts_at)}
        </p>
        {data.blurb ? (
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#0E1A13]/55">{data.blurb}</p>
        ) : null}

        <div className="mt-8 flex flex-wrap gap-3">
          <button type="button" data-testid="manage-move-btn"
                  onClick={() => { setMode(mode === "move" ? "" : "move"); setError(""); }}
                  className={pill(mode === "move")}>
            Pick another time
          </button>
          <button type="button" data-testid="manage-cancel-btn"
                  onClick={() => { setMode(mode === "cancel" ? "" : "cancel"); setError(""); }}
                  className={pill(mode === "cancel")}>
            Cancel the meeting
          </button>
        </div>

        {error ? (
          <p className="mt-6 text-sm text-[#9A3A2A]" data-testid="manage-action-error">{error}</p>
        ) : null}

        {mode === "move" ? (
          <div className="mt-8 space-y-6" data-testid="manage-slots">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0E1A13]/40">
              Times shown in your own timezone
            </p>
            {byDay.map(([day, slots]) => (
              <div key={day}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0E1A13]/40">
                  {dayLabel(slots[0])}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {slots.map((stamp) => (
                    <button key={stamp} type="button" data-testid={`manage-slot-${stamp}`}
                            onClick={() => setSlot(stamp)} className={pill(slot === stamp)}>
                      {timeLabel(stamp)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {byDay.length === 0 ? (
              <p className="text-sm text-[#0E1A13]/50" data-testid="manage-no-slots">
                No other times are open just now — email kyle@ohcbenefits.com and I'll make one.
              </p>
            ) : null}
            {slot ? (
              <button type="button" disabled={busy} data-testid="manage-confirm-move"
                      onClick={() => act("reschedule", { starts_at: slot })}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0E1A13] px-6 py-3 text-sm font-medium text-[#F7F4EC] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#9A6A3A] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Move to {dayLabel(slot)} at {timeLabel(slot)}
              </button>
            ) : null}
          </div>
        ) : null}

        {mode === "cancel" ? (
          <div className="mt-8 space-y-4 rounded-2xl border border-[#0E1A13]/10 bg-white p-6"
               data-testid="manage-cancel-form">
            <p className="text-sm leading-relaxed text-[#0E1A13]/70">
              Cancelling your {booking.type_name.toLowerCase()} on {dayLabel(booking.starts_at)} at{" "}
              {timeLabel(booking.starts_at)}. Anything you'd like us to know is welcome but not
              needed.
            </p>
            <textarea rows={3} value={reason} data-testid="manage-cancel-reason"
                      placeholder="Reason (optional)"
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full rounded-lg border border-[#1b2a22]/15 bg-white px-4 py-3 text-sm text-[#0E1A13] outline-none transition-colors duration-200 placeholder:text-[#0E1A13]/30 focus:border-[#C8A24A]" />
            <button type="button" disabled={busy} data-testid="manage-confirm-cancel"
                    onClick={() => act("cancel", { reason: reason || null })}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9A3A2A] px-6 py-3 text-sm font-medium text-[#F7F4EC] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Yes, cancel it
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
